import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CustomerSitesCard } from './CustomerSitesCard'
import {
  PageHeader,
  Alert,
  FormField,
  card,
  colors,
  input,
  label as labelStyle,
  btnPrimary,
  btnSecondary,
  btnDanger,
} from '../../components/ui'
import { api, ApiError } from '../../services/api'

interface AdminUser {
  readonly id: string
  readonly email: string
  readonly firstName: string
  readonly lastName: string
  readonly status: 'active' | 'inactive' | 'suspended'
}

interface OrganizationRow {
  readonly id: string
  readonly name: string
  readonly contactName: string | null
  readonly contactEmail: string | null
  readonly contactPhone: string | null
  readonly street: string | null
  readonly city: string | null
  readonly state: string | null
  readonly zip: string | null
  readonly country: string | null
  readonly cliaNumber: string | null
  readonly labDirector: string | null
  readonly npi: string | null
  readonly isArchived: boolean
}

interface CustomerDetail {
  readonly organization: OrganizationRow
  readonly admins: readonly AdminUser[]
}

// Editable organization fields shown on the detail page, in render order.
const ORG_FIELDS = [
  { key: 'name', label: 'Organization name', type: 'text' },
  { key: 'contactEmail', label: 'Contact email', type: 'email' },
  { key: 'contactName', label: 'Contact name', type: 'text' },
  { key: 'contactPhone', label: 'Contact phone', type: 'text' },
  { key: 'street', label: 'Street', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State', type: 'text' },
  { key: 'zip', label: 'ZIP', type: 'text' },
  { key: 'country', label: 'Country', type: 'text' },
  { key: 'cliaNumber', label: 'CLIA number', type: 'text' },
  { key: 'labDirector', label: 'Lab director', type: 'text' },
  { key: 'npi', label: 'NPI', type: 'text' },
] as const

type OrgFieldKey = (typeof ORG_FIELDS)[number]['key']

function orgFormFromRow(org: OrganizationRow): Record<OrgFieldKey, string> {
  return ORG_FIELDS.reduce(
    (acc, f) => ({ ...acc, [f.key]: (org[f.key] as string | null) ?? '' }),
    {} as Record<OrgFieldKey, string>,
  )
}

// medflow_admin-only. Edit an existing customer's organization fields and
// manage its customer_admin login(s): update name/email/status and reset
// the password (handed off out of band, same as at creation).
export function FleetCustomerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<CustomerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [orgForm, setOrgForm] = useState<Record<OrgFieldKey, string>>(() => orgFormFromRow({} as OrganizationRow))
  const [orgSaving, setOrgSaving] = useState(false)
  const [orgSaved, setOrgSaved] = useState(false)
  const [archiving, setArchiving] = useState(false)

  const refresh = useCallback(async (): Promise<void> => {
    if (!id) return
    setLoading(true)
    try {
      const res = await api.get<CustomerDetail>('/fleet/customers/' + id)
      if (res.data) {
        setDetail(res.data)
        setOrgForm(orgFormFromRow(res.data.organization))
      }
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load customer')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    refresh()
  }, [refresh])

  async function handleArchiveToggle(): Promise<void> {
    if (!id || !detail) return
    const archive = !detail.organization.isArchived
    if (archive && !window.confirm('Archive this customer? They will be hidden from the active customers list.')) {
      return
    }
    setArchiving(true)
    setError(null)
    try {
      await api.post('/fleet/customers/' + id + (archive ? '/archive' : '/unarchive'), {})
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update archive state')
    } finally {
      setArchiving(false)
    }
  }

  async function handleOrgSave(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!id) return
    setOrgSaving(true)
    setOrgSaved(false)
    setError(null)
    try {
      // Send non-empty trimmed values (mirrors the create form, where
      // optional fields are omitted rather than blanked).
      const payload: Record<string, string> = {}
      for (const f of ORG_FIELDS) {
        const v = orgForm[f.key].trim()
        if (v) payload[f.key] = v
      }
      await api.patch('/fleet/customers/' + id, payload)
      setOrgSaved(true)
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save organization')
    } finally {
      setOrgSaving(false)
    }
  }

  return (
    <div>
      <PageHeader
        title={detail ? detail.organization.name : 'Customer'}
        subtitle="Edit organization details and manage the admin login"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {detail && (
              <button
                type="button"
                onClick={handleArchiveToggle}
                disabled={archiving}
                style={detail.organization.isArchived ? btnSecondary : btnDanger}
              >
                {archiving ? 'Working...' : detail.organization.isArchived ? 'Unarchive customer' : 'Archive customer'}
              </button>
            )}
            <button type="button" onClick={() => navigate('/fleet/customers')}>
              Back to Customers
            </button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}

      {loading || !detail ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <form onSubmit={handleOrgSave} style={{ ...card, marginBottom: '1rem', maxWidth: '640px' }}>
            <h3 style={{ marginTop: 0 }}>Organization</h3>
            {orgSaved && <Alert type="success" message="Organization updated." onDismiss={() => setOrgSaved(false)} />}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {ORG_FIELDS.map((f) => (
                <FormField
                  key={f.key}
                  name={f.key}
                  label={f.label}
                  type={f.type}
                  required={f.key === 'name'}
                  value={orgForm[f.key]}
                  onChange={(v) => setOrgForm((prev) => ({ ...prev, [f.key]: v }))}
                />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="submit" disabled={orgSaving} style={btnPrimary}>
                {orgSaving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>

          <div style={{ ...card, maxWidth: '640px' }}>
            <h3 style={{ marginTop: 0 }}>Admin login{detail.admins.length === 1 ? '' : 's'}</h3>
            {detail.admins.length === 0 ? (
              <div style={{ color: colors.textMuted }}>No active admin user for this customer.</div>
            ) : (
              detail.admins.map((admin) => (
                <AdminCard key={admin.id} admin={admin} onSaved={refresh} onError={setError} />
              ))
            )}
          </div>

          <CustomerSitesCard orgId={id!} />
        </>
      )}
    </div>
  )
}

const STATUSES: readonly AdminUser['status'][] = ['active', 'inactive', 'suspended']

// One customer_admin login: edit name/email/status, reset password.
function AdminCard({
  admin,
  onSaved,
  onError,
}: {
  readonly admin: AdminUser
  readonly onSaved: () => Promise<void> | void
  readonly onError: (msg: string) => void
}) {
  const [form, setForm] = useState({
    firstName: admin.firstName,
    lastName: admin.lastName,
    email: admin.email,
    status: admin.status as AdminUser['status'],
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [resetting, setResetting] = useState(false)
  const [resetDone, setResetDone] = useState(false)

  async function handleSave(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    try {
      await api.put('/users/' + admin.id, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        status: form.status,
      })
      setSaved(true)
      await onSaved()
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Failed to update admin user')
    } finally {
      setSaving(false)
    }
  }

  async function handleReset(): Promise<void> {
    setResetting(true)
    setResetDone(false)
    try {
      await api.post('/users/' + admin.id + '/reset-password', { newPassword })
      setResetDone(true)
      setNewPassword('')
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Failed to reset password')
    } finally {
      setResetting(false)
    }
  }

  return (
    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem', marginTop: '1rem' }}>
      {saved && <Alert type="success" message="Admin user updated." onDismiss={() => setSaved(false)} />}
      <form onSubmit={handleSave}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <FormField
            name={`firstName-${admin.id}`}
            label="First name"
            value={form.firstName}
            onChange={(v) => setForm((p) => ({ ...p, firstName: v }))}
          />
          <FormField
            name={`lastName-${admin.id}`}
            label="Last name"
            value={form.lastName}
            onChange={(v) => setForm((p) => ({ ...p, lastName: v }))}
          />
          <FormField
            name={`email-${admin.id}`}
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm((p) => ({ ...p, email: v }))}
          />
          <FormField name={`status-${admin.id}`} label="Status">
            <select
              id={`status-${admin.id}`}
              style={input}
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as AdminUser['status'] }))}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
          <button type="submit" disabled={saving} style={btnSecondary}>
            {saving ? 'Saving...' : 'Save admin'}
          </button>
        </div>
      </form>

      <div style={{ marginTop: '1rem' }}>
        <label style={labelStyle}>Reset password</label>
        {resetDone && (
          <Alert
            type="success"
            message="Password reset. Hand the new password to the admin out of band."
            onDismiss={() => setResetDone(false)}
          />
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="password"
            style={{ ...input, flex: 1 }}
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <button
            type="button"
            disabled={resetting || newPassword.length === 0}
            style={btnSecondary}
            onClick={handleReset}
          >
            {resetting ? 'Resetting...' : 'Reset'}
          </button>
        </div>
      </div>
    </div>
  )
}
